import { ApolloError } from '@apollo/client';
/**
 * A permanent rejection is a business decision by the backend, not a
 * transport hiccup: `error-handler.middleware.ts` rethrows every
 * `BilderitExpectedError` (including authz denials) with
 * `extensions.isBilderitExpectedError: true`; anything else (an unexpected
 * exception) surfaces as a generic "Internal Server Error" GraphQL error
 * WITHOUT that flag.
 *
 * Rule: an error is a permanent rejection iff `graphQLErrors` is non-empty
 * AND every one of them carries the flag. A network error, an internal
 * error, a mix of flagged/unflagged errors, or any other shape is
 * transient — retention wins by default so nothing queued is ever discarded
 * on an ambiguous or infrastructure failure.
 */
export const isPermanentRejection = (graphQLErrors) => !!graphQLErrors &&
    graphQLErrors.length > 0 &&
    graphQLErrors.every((error) => { var _a; return ((_a = error.extensions) === null || _a === void 0 ? void 0 : _a.isBilderitExpectedError) === true; });
/**
 * Classifies an error thrown by `client.mutate` during offline replay.
 * Only a genuine `ApolloError` with GraphQL errors that are ALL flagged
 * `isBilderitExpectedError`, and NO network error, counts as permanent.
 * Everything else (network errors, internal server errors, mixed
 * flagged/unflagged errors, unknown shapes, or a non-ApolloError) is
 * transient and must keep the entry queued.
 */
export const classifyReplayError = (error) => {
    if (error instanceof ApolloError &&
        !error.networkError &&
        isPermanentRejection(error.graphQLErrors)) {
        return {
            kind: 'permanent',
            message: error.graphQLErrors.map((e) => e.message).join(' · '),
        };
    }
    return { kind: 'transient' };
};
